import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Eye, Shield, Trash2, UserCheck, UserX } from 'lucide-react';
import type { OrgMember } from '@examing/types';
import { BottomSheet } from 'components/mobile/primitives';
import { M } from 'components/mobile/theme';
import { ASSIGNABLE_ROLES, memberStatus, roleLabel, type MemberActions } from './memberLabels';

interface ActionRowProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  expanded?: boolean;
}

function ActionRow({ icon, label, onClick, danger = false, expanded }: ActionRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className="flex h-12 w-full items-center gap-3 text-left text-[15px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      style={{ color: danger ? M.red : M.ink }}
    >
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center" style={{ color: danger ? M.redBright : M.ink3 }}>
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
      {expanded != null && (
        <ChevronDown
          className="h-4 w-4 flex-shrink-0 transition-transform"
          style={{ color: M.faint, transform: expanded ? 'rotate(180deg)' : undefined }}
          aria-hidden
        />
      )}
    </button>
  );
}

interface MemberActionsSheetProps {
  /** The member whose row was tapped; null keeps the sheet closed. */
  member: OrgMember | null;
  onClose: () => void;
  actions: MemberActions;
}

/** A tapped phone row's actions — the same ones, with the same limits, as the desktop row menu. */
export function MemberActionsSheet({ member, onClose, actions }: MemberActionsSheetProps) {
  const { t } = useTranslation();
  const [rolesOpen, setRolesOpen] = useState(false);

  // Each member starts with the role list folded.
  useEffect(() => setRolesOpen(false), [member?.id]);

  if (!member) return null;
  const name = member.user?.name;
  const contact = member.user?.email || member.user?.phone;
  const suspended = memberStatus(member) === 'SUSPENDED';
  // Every action hands off to the page (a confirm dialog or a request), so the sheet closes first.
  const run = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <BottomSheet open onClose={onClose}>
      <div className="mb-2">
        <p className="truncate text-[15px] font-bold" style={{ color: M.ink }}>
          {name || contact || '—'}
        </p>
        <p className="truncate text-[12.5px]" style={{ color: M.ink3 }}>
          {[name ? contact : null, roleLabel(member.role, t), member.isOwner ? t('group_role_owner') : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      <div className="divide-y divide-[#F3F4F6]">
        <ActionRow icon={<Eye size={18} />} label={t('view_details')} onClick={run(() => actions.onView(member))} />
        {!member.isOwner && (
          <div>
            <ActionRow
              icon={<Shield size={18} />}
              label={t('change_role')}
              expanded={rolesOpen}
              onClick={() => setRolesOpen((open) => !open)}
            />
            {rolesOpen && (
              <div role="radiogroup" aria-label={t('change_role')} className="pb-2 pl-8">
                {ASSIGNABLE_ROLES.map((r) => {
                  const current = member.role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      role="radio"
                      aria-checked={current}
                      disabled={current}
                      onClick={run(() => actions.onChangeRole(member, r.value))}
                      className="flex h-11 w-full items-center gap-3 text-left text-[15px] disabled:cursor-default"
                      style={{ color: M.ink }}
                    >
                      <span
                        aria-hidden
                        className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
                        style={{ border: `1.5px solid ${current ? M.ink : M.chevron}` }}
                      >
                        {current && <span className="h-2.5 w-2.5 rounded-full" style={{ background: M.ink }} />}
                      </span>
                      <span className="min-w-0 flex-1 truncate" style={{ fontWeight: current ? 600 : 400 }}>
                        {t(r.labelKey)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <ActionRow
          icon={suspended ? <UserCheck size={18} /> : <UserX size={18} />}
          label={suspended ? t('activate') : t('suspend')}
          onClick={run(() => actions.onToggleSuspend(member))}
        />
        {!member.isOwner && (
          <ActionRow icon={<Trash2 size={18} />} label={t('remove')} danger onClick={run(() => actions.onRemove(member))} />
        )}
      </div>
    </BottomSheet>
  );
}

export default MemberActionsSheet;
